import React, { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SidePanel } from '@securspace/securspace-ui-kit';
import { ApiCredentials } from '../types';
import CreateTokenForm from './CreateTokenForm';

interface CreateTokenPanelProps {
  open: boolean;
  onClose: () => void;
  onTokenCreated?: () => void;
}

/**
 * Side panel container for the Create API Token form.
 * Manages panel open/close state and the confirmation dialog for unsaved credentials.
 */
const CreateTokenPanel: React.FC<CreateTokenPanelProps> = ({
  open,
  onClose,
  onTokenCreated,
}) => {
  // Credentials state (lifted up to manage close confirmation)
  const [credentials, setCredentials] = useState<ApiCredentials | null>(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  // Dialog state
  const [confirmCloseDialog, setConfirmCloseDialog] = useState(false);
  const [discardChangesDialog, setDiscardChangesDialog] = useState(false);

  const resetState = () => {
    setCredentials(null);
    setSavedConfirmed(false);
    setFormDirty(false);
    setConfirmCloseDialog(false);
    setDiscardChangesDialog(false);
  };

  const handleClose = (forceReset: boolean = false) => {
    if (forceReset) {
      resetState();
    } else {
      setConfirmCloseDialog(false);
      setDiscardChangesDialog(false);
    }
    onClose();
  };

  const handleCloseAttempt = useCallback((): boolean => {
    if (credentials && !savedConfirmed) {
      setConfirmCloseDialog(true);
      return false;
    }
    if (formDirty && !credentials) {
      setDiscardChangesDialog(true);
      return false;
    }
    resetState();
    return true;
  }, [credentials, savedConfirmed, formDirty]);

  const handleCredentialsCreated = (newCredentials: ApiCredentials) => {
    setCredentials(newCredentials);
  };

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        onCloseAttempt={handleCloseAttempt}
        title="Create API Token"
      >
        {/* Form Content & Footer */}
        <CreateTokenForm
          onTokenCreated={onTokenCreated}
          onClose={handleClose}
          onCredentialsCreated={handleCredentialsCreated}
          onSavedConfirmedChange={setSavedConfirmed}
          onFormDirtyChange={setFormDirty}
          savedConfirmed={savedConfirmed}
          credentials={credentials}
        />
      </SidePanel>

      {/* Confirm Close Dialog - Unsaved Credentials */}
      <Dialog
        open={confirmCloseDialog}
        onClose={() => setConfirmCloseDialog(false)}
        aria-labelledby="confirm-close-title"
      >
        <DialogTitle id="confirm-close-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Close Without Saving?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your <strong>secret key will be lost forever</strong> if you close this panel without copying it.
            This secret cannot be retrieved later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmCloseDialog(false)}
            variant="contained"
            color="primary"
          >
            Go Back & Copy
          </Button>
          <Button
            onClick={() => {
              setConfirmCloseDialog(false);
              handleClose(true);
            }}
            color="error"
          >
            Close Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard Changes Dialog - Unsaved Form Data */}
      <Dialog
        open={discardChangesDialog}
        onClose={() => setDiscardChangesDialog(false)}
        aria-labelledby="discard-changes-title"
      >
        <DialogTitle id="discard-changes-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Discard Unsaved Changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes in the form. Are you sure you want to close and lose your progress?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDiscardChangesDialog(false)}
            variant="contained"
            color="primary"
          >
            Keep Editing
          </Button>
          <Button
            onClick={() => {
              setDiscardChangesDialog(false);
              handleClose(true);
            }}
            color="error"
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateTokenPanel;
